import React from "react";

// MUI IMPORTS
import {
  Box,
  CardContent,
  Typography,
  Button,
  Stack,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";

import {
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ErrorOutline as ErrorIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";

export default function SuccessScreen({ data }) {
  const hayErrores = data?.totalOmitidos > 0; //
  const listaErrores = data?.erroresValidacion || []; //

  const theme = createTheme({
    palette: {
      primary: {
        main: "#1565c0",
      },
      secondary: {
        main: "#43a047",
      },
      error: {
        main: "#e53935",
        light: "#ef5350",
      },
      warning: {
        main: "#ed6c02",
      },
      background: {
        default: "#f5f7fa",
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: "none",
            fontWeight: 600,
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CardContent sx={{ p: 3 }}>
        {hayErrores ? (
          <Stack spacing={2.5} alignItems="center">
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: "50%",
                bgcolor: "rgba(229, 57, 53, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningIcon sx={{ fontSize: 42, color: "error.main" }} />
            </Box>

            <Stack spacing={1} alignItems="center">
              <Typography
                variant="h5"
                fontWeight={600}
                color="error.main"
                textAlign="center"
              >
                Descarga Interrumpida
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
                sx={{ maxWidth: 550 }}
              >
                Se {data?.totalOmitidos === 1 ? "ha omitido 1 registro" : `han omitido ${data?.totalOmitidos} registros`} por errores en la validación de datos.
              </Typography>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                width: "100%",
                p: 2,
                bgcolor: "#fff4e5",
                borderRadius: 2,
                border: "1px solid #ffe0b2",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="warning.dark" fontWeight={500}>
                Por favor, póngase en contacto con el área de <strong>Sistemas</strong> para la corrección del error. Una vez solucionado, vuelva a intentar el proceso.
              </Typography>
            </Paper>

            {listaErrores.length > 0 && (
              <Accordion
                elevation={0}
                defaultExpanded
                sx={{
                  width: "100%",
                  bgcolor: "#fff5f5",
                  border: "1px solid #fecdd3",
                  borderRadius: "10px !important",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "error.main" }} />}
                  sx={{ px: 2.5 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ErrorIcon color="error" fontSize="small" />
                    <Typography color="error.main" fontWeight={600} variant="subtitle2">
                      Ver detalle de registros omitidos ({listaErrores.length})
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 1.5, pt: 0 }}>
                  <List disablePadding sx={{ maxHeight: 180, overflowY: "auto" }}>
                    {listaErrores.map((err, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <Divider component="li" />}
                        <ListItem alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                {err.razonSocial}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" color="error.main" sx={{ mt: 0.3 }}>
                                {err.motivo}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              sx={{ mt: 2, width: "100%" }}
            >
              <Button
                variant="outlined"
                size="large"
                startIcon={<HomeIcon />}
                onClick={() => {
                  window.location.href = "/#/navbar";
                }}
                sx={{ minWidth: 180 }}
              >
                Ir al Inicio
              </Button>

              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  window.location.reload();
                }}
                sx={{ minWidth: 200 }}
              >
                Volver a Intentar
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: "50%",
                bgcolor: "rgba(67, 160, 71, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckIcon sx={{ fontSize: 42, color: "secondary.main" }} />
            </Box>

            <Stack spacing={0.5} alignItems="center">
              <Typography
                variant="h5"
                fontWeight={600}
                color="text.primary"
                textAlign="center"
              >
                Archivo generado exitosamente
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Archivos descargados con éxito.
              </Typography>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                width: "100%",
                p: 2,
                bgcolor: "#e8f5e9",
                borderRadius: 3,
                border: "1px solid #a5d6a7",
                textAlign: "center",
                mt: 1,
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1 }}
              >
                Registros procesados exitosamente
              </Typography>
              <Typography
                variant="h4"
                fontWeight={700}
                color="#2e7d32"
                sx={{ mt: 0.5 }}
              >
                {data?.totalExitosos}
              </Typography>
            </Paper>

            <Box sx={{ mt: 1.5, textAlign: "center", width: "100%" }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                ¿Qué deseas hacer a continuación?
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                sx={{ mt: 1.5 }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<HomeIcon />}
                  onClick={() => {
                    window.location.href = "/#/navbar";
                  }}
                  sx={{ minWidth: 180 }}
                >
                  Ir al Inicio
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    window.location.reload();
                  }}
                  sx={{ minWidth: 200 }}
                >
                  Generar Nuevo Archivo
                </Button>
              </Stack>
            </Box>
          </Stack>
        )}
      </CardContent>
    </ThemeProvider>
  );
}

